import React from 'react';
import { NavLink } from 'react-router-dom';
import { HiOutlineLogout, HiX } from 'react-icons/hi';

const allNavGroups = [
  {
    label: 'MAIN',
    items: [
      { to: '/dashboard', label: 'Dashboard', emoji: '📊', feature: 'dashboard' },
      { to: '/calendar', label: 'Calendar', emoji: '📅', badge: 3, feature: 'calendar' },
      { to: '/notifications', label: 'Notifications', emoji: '🔔', badge: 7, feature: 'notifications' },
    ],
  },
  {
    label: 'MANAGEMENT',
    items: [
      { to: '/contracts', label: 'Contracts (MOUs)', emoji: '📄', feature: 'contracts' },
      { to: '/quotations', label: 'Quotations', emoji: '💰', feature: 'quotations' },
      { to: '/services', label: 'Work Management', emoji: '✨', feature: 'services' },
      { to: '/visitors', label: 'Visitors', emoji: '🚪', feature: 'visitors' },
      { to: '/members', label: 'Apartment Members', emoji: '🏠', feature: 'members' },
      { to: '/users', label: 'Users', emoji: '🔑', superadminOnly: true },
    ],
  },
  {
    label: 'FACILITY MANAGEMENT & COMPLIANCE',
    items: [
      { to: '/facility-compliance', label: 'Overview', emoji: '🏢', feature: 'facility_management', caretakerVisible: true },
    ],
  },
  {
    label: 'ACCOUNTS',
    items: [
      { to: '/payments', label: 'Payment Tracker', emoji: '💳', feature: 'payments' },
    ],
  },
  {
    label: 'BOOKINGS',
    items: [
      { to: '/bookings', label: 'Facility Booking', emoji: '🏸', feature: 'bookings' },
    ],
  },
  {
    label: 'SERVICES',
    items: [
      { to: '/security', label: 'Security', emoji: '🛡️', feature: 'security' },
      { to: '/cleaning', label: 'Cleaning', emoji: '🧹', feature: 'cleaning' },
      { to: '/water-treatment', label: 'Water Treatment', emoji: '💧', feature: 'water_treatment' },
      { to: '/gas-readings', label: 'Gas Readings', emoji: '🔥', feature: 'gas_readings' },
    ],
  },
];

const gasReaderGroups = [
  {
    label: 'MAIN',
    items: [
      { to: '/gas-readings', label: 'Gas Readings', emoji: '🔥' },
    ],
  },
];

function getFilteredGroups(user) {
  if (user?.role === 'gas_reader') {
    return gasReaderGroups;
  }

  const isSuperAdmin  = user?.role === 'superadmin';
  const isAdmin       = user?.role === 'admin';
  const isCaretaker   = user?.role === 'caretaker';
  const isCustomRole  = user?.role === 'custom';
  const allowedFeatures = user?.allowed_features || [];

  return allNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.superadminOnly) {
          return isSuperAdmin || isAdmin;
        }
        if (item.caretakerVisible) {
          if (isCustomRole) return item.feature ? allowedFeatures.includes(item.feature) : false;
          if (isSuperAdmin || isCaretaker) return true;
          // Admin: respect allowed_features like any other feature item
          if (!item.feature) return true;
          return allowedFeatures.includes(item.feature);
        }
        if (isSuperAdmin) {
          return true;
        }
        // Custom role users: only show items with an explicit feature they're allowed
        if (isCustomRole) {
          return item.feature ? allowedFeatures.includes(item.feature) : false;
        }
        if (!item.feature) {
          return true;
        }
        return allowedFeatures.includes(item.feature);
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export default function Sidebar({ user, onLogout, open, onClose }) {
  const groups = getFilteredGroups(user);

  return (
    <div
      className={`fixed left-0 top-0 h-screen w-[280px] text-white flex flex-col overflow-y-auto z-50 transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ background: 'linear-gradient(180deg, #032841 0%, #043959 100%)', boxShadow: '0 10px 25px rgba(0,0,0,0.08)' }}
    >
      {/* Logo */}
      <div className="px-6 py-8 border-b border-white/10" style={{ background: 'rgba(0,0,0,0.1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-2xl"
            style={{ background: '#F4A460', boxShadow: '0 4px 12px rgba(244,164,96,0.3)' }}>
            GC
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold" style={{ letterSpacing: '-0.02em' }}>Garden Court</h1>
            <p className="text-[11px] opacity-70 font-light uppercase" style={{ letterSpacing: '0.5px' }}>Admin Portal</p>
          </div>
          <button onClick={onClose} className="lg:hidden text-white/70 hover:text-white p-1">
            <HiX className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6">
        {groups.map((group) => (
          <div key={group.label} className="mb-8">
            <p className="px-6 mb-3 text-[11px] font-medium uppercase opacity-50" style={{ letterSpacing: '1px' }}>
              {group.label}
            </p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-[14px] transition-all border-l-[3px] ${
                    isActive
                      ? 'bg-white/[0.12] text-white border-l-accent-400 font-medium'
                      : 'text-white/80 border-l-transparent hover:bg-white/[0.08] hover:text-white hover:border-l-accent-400'
                  }`
                }
              >
                <span className="text-xl w-6 text-center leading-none">{item.emoji}</span>
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-xl"
                    style={{ background: '#F4A460', color: '#032841' }}>
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="px-6 py-5 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
            {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-[11px] opacity-50 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 mt-3 text-[13px] opacity-60 hover:opacity-100 transition-opacity"
        >
          <HiOutlineLogout className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
